import React, { useState } from 'react';
import { Card, SheetHeading, SponsorRow, SponsorRowSkeleton, PlatIcon } from './sections';
import type { SponsorDeal, UploadItem, AddSponsorInput } from './sections';
import PaperDialog from '../ui/PaperDialog';
import Doodle from '../brand/Doodle';

/**
 * The creator's sponsors — the ONE place they live. This used to be two pages that never agreed:
 * "Tracked Sponsors" on the dashboard (sponsorships) and the Sponsors tab (the codes table), each
 * with its own add form writing a different record. Now one list (mergeSponsorCodes) and one
 * "Add sponsor".
 */

/** What the add form hands the page: the sponsorship, plus the store a code works at. */
export type AddSponsorRequest = AddSponsorInput & { store?: string | null };

const SHOWN = 8;

const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode; hint?: string }> = ({ label, required, children, hint }) => (
  <label className="block">
    <span className="br-small mb-1 block font-bold">{label}{required && <span className="br-ink-red"> *</span>}</span>
    {children}
    {hint && <span className="br-caption mt-1 block">{hint}</span>}
  </label>
);
const inputCls = 'w-full px-3 py-2 text-sm';

/** The one Add-sponsor form. `needsCode`: without a YouTube connection a sponsor is filed by its
 *  code and store, so both are required; with one, a sponsor with no code can be added too. */
const AddSponsorDialog: React.FC<{
  uploads: UploadItem[]; saving?: boolean; needsCode: boolean;
  onClose: () => void; onSubmit: (input: AddSponsorRequest) => Promise<void>;
}> = ({ uploads, saving, needsCode, onClose, onSubmit }) => {
  const [brand, setBrand] = useState('');
  const [code, setCode] = useState('');
  const [store, setStore] = useState('');
  const [offer, setOffer] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);
  const canSave = brand.trim().length > 0 && (!needsCode || (code.trim().length > 0 && store.trim().length > 0)) && !saving;
  const toggle = (id: string) => setSelected(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const submit = async () => {
    if (!canSave) return;
    setErr(null);
    const videos = uploads.filter(u => selected.has(u.id)).map(u => ({ video_id: u.id, url: u.url, title: u.title, thumbnail_url: u.thumbnail }));
    try {
      await onSubmit({ brand: brand.trim(), code: code.trim() || null, store: store.trim() || null, offer: offer.trim() || null, videos });
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not add sponsor.');
    }
  };

  return (
    <PaperDialog title="Add a sponsor" eyebrow="Your sponsors" motion="writing"
      description="It joins your sponsor list."
      onClose={() => { if (!saving) onClose(); }}>
      <form className="space-y-3.5" onSubmit={e => { e.preventDefault(); void submit(); }}>
        <Field label="Brand" required><input type="text" className={inputCls} value={brand} onChange={e => setBrand(e.target.value)} placeholder="e.g. AG1" autoFocus /></Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Discount code" required={needsCode} hint="The code your audience uses at checkout">
            <input type="text" className={`${inputCls} font-mono uppercase`} value={code} onChange={e => setCode(e.target.value)} placeholder="NOVA15" />
          </Field>
          <Field label="Store website" required={needsCode} hint="Where the code works">
            <input type="text" className={inputCls} value={store} onChange={e => setStore(e.target.value)} placeholder="brand.com" inputMode="url" maxLength={300} />
          </Field>
        </div>
        <Field label="Offer / benefit"><input type="text" className={inputCls} value={offer} onChange={e => setOffer(e.target.value)} placeholder="15% off your first order" /></Field>
        {uploads.length > 0 && (
          <div>
            <span className="br-small mb-1.5 block font-bold">Featured in videos</span>
            <div className="br-sheet br-sheet--cream br-sheet--plain br-ruled max-h-44 overflow-y-auto px-2.5">
              {/* each video is a printed box the creator ticks with the pen */}
              {uploads.map(u => (
                <label key={u.id} className="flex cursor-pointer items-center gap-2.5 py-1.5">
                  <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggle(u.id)} className="br-check" />
                  <PlatIcon id={u.platform} size={13} />
                  <span className="min-w-0 flex-1 truncate text-xs text-text-primary">{u.title}</span>
                </label>
              ))}
            </div>
          </div>
        )}
        {needsCode && <p className="br-caption">Connect YouTube in Settings to add a sponsor that has no code.</p>}
        {err && <p role="alert" className="br-small br-ink-red">{err}</p>}
        <hr className="br-rule !mt-5" />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={saving} className="br-slab br-slab--cream br-slab--sm">Cancel</button>
          <button type="submit" disabled={!canSave} className="br-slab br-slab--blue br-slab--sm">{saving ? 'Adding…' : 'Add sponsor'}</button>
        </div>
      </form>
    </PaperDialog>
  );
};

export default function SponsorLedger({
  deals, loading = false, uploads, onAddSponsor, savingSponsor, needsCode = false,
}: {
  deals: SponsorDeal[]; loading?: boolean; uploads: UploadItem[];
  onAddSponsor?: (input: AddSponsorRequest) => Promise<void>; savingSponsor?: boolean;
  /** True when the creator can only file a sponsor by its code and store (no YouTube connection). */
  needsCode?: boolean;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? deals : deals.slice(0, SHOWN);
  const addButton = onAddSponsor
    ? <button type="button" onClick={() => setAdding(true)} className="br-slab br-slab--blue br-slab--sm">Add sponsor</button>
    : null;

  return (
    <>
      <Card>
        <SheetHeading title="Brands and codes"
          sub="Found in your videos or added by you."
          // with nothing filed, the one blue slab sits on the empty sheet below instead
          right={deals.length > 0 ? addButton : undefined} />
        {loading ? (
          <div className="br-ruled">{[0, 1, 2].map(i => <SponsorRowSkeleton key={i} />)}</div>
        ) : deals.length > 0 ? (
          <>
            <div className="br-ruled">
              {shown.map((d, i) => {
                const key = `${d.brand}-${d.code || ''}-${i}`;
                return (
                  <SponsorRow key={key} deal={d} open={expanded === key}
                    onToggle={() => setExpanded(x => (x === key ? null : key))}
                    action={d.expires ? (
                      <div className="br-sponsor-actions">
                        <span className="br-caption">Expires {d.expires}</span>
                      </div>
                    ) : undefined} />
                );
              })}
            </div>
            {deals.length > SHOWN && (
              <button type="button" className="br-inkline mt-3 text-sm" onClick={() => setShowAll(v => !v)}>
                {showAll ? 'Show fewer' : `Show all ${deals.length} sponsors`}
              </button>
            )}
          </>
        ) : (
          // nothing filed yet: the blank tag is the object that would be filled in once a brand
          // is, drawn beside the line that says so
          <div className="br-ruled-empty br-empty-drawn">
            <Doodle name="tag" />
            <div className="br-empty-drawn__copy">
              <p>No sponsors yet. They appear automatically once your uploads are analyzed — or add one you already have.</p>
              {addButton}
            </div>
          </div>
        )}
      </Card>
      {adding && onAddSponsor && (
        <AddSponsorDialog uploads={uploads} saving={savingSponsor} needsCode={needsCode}
          onClose={() => setAdding(false)} onSubmit={onAddSponsor} />
      )}
    </>
  );
}
